import { type ThunkAction } from "redux-thunk";
import { type LineItem, PackageEntrySource } from "../../models/CartData";
import { type AnyAction } from "~/app/store/types";
import { navigateToProductContract } from "~/features/contracts/utils/utils";
import { requestPriceData } from "~/domains/product/redux/actions";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { getNextUID } from "~/common/utils/uid";
import { fetchContractsOverview } from "~/features/contracts/api/getContractsOverview";
import { orchestrateARefreshOnCheckout, orchestratePnARefresh } from "./cartRefresh";
import {
  buildCartMaterialNos,
  newChangeObject,
  newCopyRowObject,
  newUpdatesObject,
} from "../../../../../utils/cartUpdating/common";
import { refreshPriceInfoData } from "../../../../../utils/cartUpdating/refreshPriceInfoData";
import { refreshDeliveriesForProduct } from "../../../../../utils/cartUpdating/delivery/refreshEdd";
import { sendToHybris, sendToRedux } from "./cartQuoteCommonActions";
import { type RootState } from "~/app/store";
import { getDesciptorsForLineItems } from "./actionUtils";
import { selectLineItemsByProduct, selectPackageEntry, selectProductCodeFromPackage } from "../selectors/cartSelectors";
import {
  MSG_COULD_NOT_PRESELECT_PACKAGE,
  MSG_MISSING_PACKAGE_REGULAR,
  MSG_PACKAGE_CHANGED,
  refreshAvailabilityLoadNonContract,
  refreshPackage,
} from "../../../../../utils/cartUpdating/refreshPackage";
import { groupByFn } from "~/common/utils/reducers";
import { validatePackage, validateQty } from "../../../../../utils/cartUpdating/validations/validateGeneralLineItem";
import { validateEntireProductQty } from "../../../../../utils/cartUpdating/validations/validateGeneralProduct";
import { getFirstValidDate } from "~/common/utils/dateUtils";
import debounce from "lodash/debounce";
import { selectLineItemsForProduct } from "~/features/cart/cart/redux/selectors/productEntrySelectors";
import { calculateSalesCampaign4PDP } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { refreshDeliveryDate } from "~/utils/cartUpdating/delivery/refreshDate";
import { cartLoadStartAction } from "~/features/cart/cart/redux/actions";
import { cartPageLoadThunk } from "~/features/cart/cart/redux/cartPageLoadThunk";
import { apiUpdateCartComment } from "~/features/cart/cart/api/updateCartComment";
import { CommentTargets, trackCommentActions } from "~/app/analytics/ga/salesPartner";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import type { LineItemBuilder } from "../../../../../utils/cartUpdating/LineItemUpdate";
import { BLOCK_SCREEN, UNBLOCK_SCREEN } from "~/features/cart/cart/redux/actions/actionTypes";
import uniqBy from "lodash/uniqBy";
import { ProductService } from "~/features/addToCart/api/ProductService";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { type AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";

export {
  deletePackageEntry,
  clearCart,
  updateQuantity,
  updateDate,
  updateComment,
  toggleCommentVisibility,
} from "./cartQuoteCommonActions";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

interface SwitchProductToNewVersionProps {
  productCode: string;
  successorProductCode: string;
  packEntryId: number;
  priceReference: string;
}

export function copyLastPackageEntry(lastEntryId: number): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState();

    const lastEntry = state.cart.packageEntriesById[lastEntryId];

    const allLineItems = selectLineItemsForProduct(state, { productCode: lastEntry.productCode }).map(newChangeObject);

    const copiedLineItem = newCopyRowObject(lastEntry);
    copiedLineItem.set("id", getNextUID());

    if (lastEntry.source !== PackageEntrySource.CONTRACT && !copiedLineItem.get("deliveryDate")) {
      copiedLineItem.set("manuallySetRDD", true);
      copiedLineItem.set("deliveryDate", getFirstValidDate());
    }

    copiedLineItem.set("edd", null);
    copiedLineItem.set("isInStock", null);
    copiedLineItem.set("customerOrderNumber", "");
    if (copiedLineItem.get("isBundled")) {
      copiedLineItem.set("original", false);
    }
    allLineItems.push(copiedLineItem);

    const nonContractLineItems = allLineItems.filter((item) => !item.get("contractNumber"));

    const productDescriptors = getDesciptorsForLineItems(state, lastEntry.productCode, allLineItems);
    refreshPriceInfoData(allLineItems, productDescriptors);

    const productDescriptor = productDescriptors[copiedLineItem.get("priceReference")];
    const availability = productDescriptor.availability;
    if (availability) {
      refreshDeliveriesForProduct(nonContractLineItems, availability);
    }

    validateEntireProductQty(allLineItems, productDescriptor, buildCartMaterialNos(state));

    dispatch(sendToRedux(allLineItems));
    dispatch(cartLoadStartAction());
    await dispatch(sendToHybris(allLineItems));
    dispatch(cartPageLoadThunk());

    if (lastEntry.source !== PackageEntrySource.CONTRACT) {
      const lineItem = copiedLineItem.build() as LineItem;
      AnalyticsMQ.instance.push("addToCart", {
        products: {
          product: productDescriptor.base,
          packaging: {
            code: lineItem.packCode,
            size: lineItem.packSize ?? 1,
          },
          price: lineItem.netPrice,
          quantity: lineItem.quantity,
        },
        context: {
          source: "unknown",
          recommendationType: lineItem.recoType,
          recommendationPlace: lineItem.recoPlace,
        },
      });
    }
  };
}

export const switchProductVersion = (): ThunkResult<Promise<void>> => {
  return async (dispatch, getState) => {
    const state = getState();
    const packEntries = Object.values(state.cart.packageEntriesById);
    const lineItemsToUpdate: LineItemBuilder[] = [];
    const modifiablePackEntries: SwitchProductToNewVersionProps[] = [];

    packEntries.map(async (packEntry) => {
      const productCode = packEntry.productCode;
      const productDescriptor = selectProductDescriptor(state, {
        productCode,
        priceReference: packEntry.priceReference,
      });
      const successorProduct = productDescriptor?.base?.successorProduct;
      if (successorProduct) {
        const successorProductCode = successorProduct.code;
        modifiablePackEntries.push({
          productCode,
          successorProductCode,
          packEntryId: packEntry.id,
          priceReference: packEntry.priceReference,
        });
      }
    });

    await Promise.all(
      modifiablePackEntries.map(async ({ successorProductCode, priceReference }) => {
        return ProductService.getProductDescriptor(successorProductCode, priceReference, {
          fetch: true,
        }).promise as Promise<ProductDescriptor>;
      }),
    );

    modifiablePackEntries.map(async ({ productCode, successorProductCode, packEntryId, priceReference }) => {
      const lineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);
      const editedLineItem = lineItems.find((item) => item.get("id") === packEntryId);

      if (editedLineItem) {
        editedLineItem.set("successorProductCode", successorProductCode);
        editedLineItem.set("packCode", INVALID_PACKAGE_CODE);
        lineItemsToUpdate.push(editedLineItem);

        const productDescriptors = getDesciptorsForLineItems(getState(), successorProductCode, [editedLineItem]);
        const availability = productDescriptors[priceReference].availability as AvailabilityInfo;
        refreshPriceInfoData([editedLineItem], productDescriptors);
        refreshAvailabilityLoadNonContract(editedLineItem, availability, productDescriptors[priceReference]);
      }
    });

    dispatch(sendToRedux(lineItemsToUpdate));
    const switchProductPromise = dispatch(sendToHybris(lineItemsToUpdate));
    switchProductPromise.then((res) => {
      if (res) {
        const url = new URL(window.location.href);
        url.searchParams.set("switched-products-count", `${uniqBy(modifiablePackEntries, "productCode").length}`);
        window.history.replaceState({}, "", url);
        window.location.reload();
      }
    });
  };
};

export const updatePriceReferenceForCart = (
  priceReference: string,
  packEntryIds: number[],
  handleOpenPriceRefSnackBar: (count: number) => void,
): ThunkResult<void> => {
  return async (dispatch, getState) => {
    const state = getState();
    const lineItemsToUpdate: LineItemBuilder[] = [];
    const modifiablePackEntries: { productCode: string; packEntryId: number }[] = [];

    dispatch({ type: BLOCK_SCREEN, payload: "spinner.updatePriceReferenceForCart" });

    await Promise.all(
      packEntryIds.map((packEntryId) => {
        const packageEntry = state.cart.packageEntriesById[packEntryId];
        const productCode = packageEntry.productCode;

        // Skip if sales are enabled for this package entry
        if (packageEntry.saleCampaignEnabled) {
          return;
        }
        modifiablePackEntries.push({ productCode, packEntryId });

        return dispatch(requestPriceData(productCode, priceReference));
      }),
    );

    modifiablePackEntries.map(({ productCode, packEntryId }) => {
      const lineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);
      const editedLineItem = lineItems.find((item) => item.get("id") === packEntryId);
      if (editedLineItem) {
        editedLineItem.set("priceReference", priceReference);
        lineItemsToUpdate.push(editedLineItem);

        const productDescriptors = getDesciptorsForLineItems(getState(), productCode, [editedLineItem]);
        refreshPriceInfoData([editedLineItem], productDescriptors);
      }
    });

    dispatch(sendToRedux(lineItemsToUpdate));
    const updatePriceReferencePromise = dispatch(sendToHybris(lineItemsToUpdate));
    updatePriceReferencePromise.then(() => {
      dispatch({ type: UNBLOCK_SCREEN });
      handleOpenPriceRefSnackBar(uniqBy(modifiablePackEntries, "productCode").length);
    });
  };
};

export const updatePriceReference = (priceReference: string, packageEntryId: number): ThunkResult<void> => {
  return async (dispatch, getState) => {
    const state = getState();
    const packageEntry = state.cart.packageEntriesById[packageEntryId];
    const productCode = packageEntry.productCode;
    await dispatch(requestPriceData(productCode, priceReference));

    const lineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);
    const editedLineItem = lineItems.filter((item) => item.get("id") === packageEntryId)[0];

    editedLineItem.set("priceReference", priceReference);

    const productDescriptors = getDesciptorsForLineItems(getState(), productCode, lineItems);
    refreshPriceInfoData(lineItems, productDescriptors);

    dispatch(sendToRedux(lineItems));
    dispatch(sendToHybris(lineItems));
  };
};
export const updateSalesEnabled = (salesEnabled: boolean, packageEntryId: number): ThunkResult<void> => {
  return async (dispatch, getState) => {
    const state = getState();
    const packageEntry = state.cart.packageEntriesById[packageEntryId];
    const productCode = packageEntry.productCode;

    const productDescriptor = selectProductDescriptor(state, {
      productCode,
      priceReference: packageEntry.priceReference,
    });
    const salesData = calculateSalesCampaign4PDP({ productDescriptor });

    const lineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);
    const editedLineItem = lineItems.filter((item) => item.get("id") === packageEntryId)[0];

    editedLineItem.set("saleCampaignEnabled", salesEnabled);
    if (salesEnabled) {
      const saleForPackage = salesData?.data?.salePerPack[packageEntry.packCode];
      const salesCampaignCode = saleForPackage?.campaignCode;
      editedLineItem.set("saleCampaignCode", salesCampaignCode);
    } else {
      editedLineItem.set("saleCampaignCode", undefined);
    }

    const productDescriptors = getDesciptorsForLineItems(getState(), productCode, lineItems);
    refreshPriceInfoData(lineItems, productDescriptors);
    lineItems.forEach(refreshDeliveryDate);

    // TODO - this is a wrong order, normally we refresh edd before delivery date.
    // Need to take a step back, decide a global order of things and do one flow instead of different flows depending on the parameter that's changing
    const lineItemsByType = groupByFn(
      lineItems,
      (item) => item.get("source"),
      (item) => item,
    );
    const lineItemsWithUpdateableEDD = [
      ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
      ...(lineItemsByType[PackageEntrySource.BULK] || []),
      ...(lineItemsByType[PackageEntrySource.QUOTE] || []),
    ];
    if (productDescriptor.availability) {
      refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, productDescriptor.availability!);
    }
    validateEntireProductQty(lineItems, productDescriptor, buildCartMaterialNos(getState()));

    dispatch(sendToRedux(lineItems));
    dispatch(sendToHybris(lineItems));
  };
};

export const updatePoNumber = (customerOrderNumber: string, packageEntryId: number): ThunkResult<void> => {
  return (dispatch, getState) => {
    const state = getState();
    const packageEntry = selectPackageEntry(packageEntryId)(state);
    const lineItemUpdate = newUpdatesObject(packageEntry, { customerOrderNumber } as Partial<LineItem>);

    const list = [lineItemUpdate];

    dispatch(sendToRedux(list));
    debounceCall(() => dispatch(sendToHybris(list)));
  };
};

const debounceCall = debounce((fn: () => void) => fn(), 300);

export function navigateToProductContractAction(packEntryId: number): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const packEntry = state.cart.packageEntriesById[packEntryId];
    const productCode = packEntry.productCode;
    const designation = state.products.basic[productCode].designation;
    fetchContractsOverview().then((data) => navigateToProductContract(data, designation));
  };
}

export function loadPnACartEntry(productCode: string): ThunkResult<Promise<void>> {
  return (dispatch, getState) => {
    const state = getState();
    const lineItems = selectLineItemsByProduct(state)[productCode];

    const priceReferences: Dict<boolean> = {};
    lineItems.forEach((entry) => (priceReferences[entry.priceReference] = true));

    const allLoadedOrLoading = Object.keys(priceReferences).every((priceReference) => {
      const descriptor = selectProductDescriptor(state, {
        productCode,
        priceReference,
      });
      return descriptor.isLoading || descriptor.isLoading;
    });

    if (allLoadedOrLoading) {
      return Promise.resolve();
    }

    return dispatch(orchestratePnARefresh(lineItems, true));
  };
}
export function loadACartEntry(productCode: string): ThunkResult<Promise<void>> {
  return (dispatch, getState) => {
    const state = getState();
    const lineItems = selectLineItemsByProduct(state)[productCode];

    const priceReferences: Dict<boolean> = {};
    lineItems.forEach((entry) => (priceReferences[entry.priceReference] = true));

    const allLoadedOrLoading = Object.keys(priceReferences).every((priceReference) => {
      const descriptor = selectProductDescriptor(state, {
        productCode,
        priceReference,
      });
      return descriptor.isAvailabilityLoading;
    });

    if (allLoadedOrLoading) {
      return Promise.resolve();
    }

    return dispatch(orchestrateARefreshOnCheckout(lineItems, true));
  };
}

export function toggleShowPackaging(packEntryId: number, newValue?: boolean): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const packageEntry = selectPackageEntry(packEntryId)(state);
    // Ensure the product data is available
    const productCode = packageEntry.productCode;

    const lineItemUpdate = newUpdatesObject(packageEntry, {
      isPackagingSelectionOpen: newValue ?? !packageEntry.isPackagingSelectionOpen,
    });

    const list = [lineItemUpdate];
    dispatch(sendToRedux(list));

    const productDescriptor = selectProductDescriptor(state, {
      productCode,
      priceReference: packageEntry.priceReference,
    });
    if (!productDescriptor.isLoaded && !productDescriptor.isLoading) {
      // Load the entire Cart Entry - rarely users have multiple price references in the same product anyway
      dispatch(loadPnACartEntry(packageEntry.productCode));
    }
  };
}
export function selectPackaging(packEntryId: number, packCode: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const getPackage = (packEntryId: number) => selectPackageEntry(packEntryId)(state);
    const packageEntry = getPackage(packEntryId);

    const productCode = selectProductCodeFromPackage(packEntryId)(state);
    const productDescriptor = selectProductDescriptor(state, {
      productCode,
      priceReference: packageEntry.priceReference,
    });

    const allLineItems = selectLineItemsByProduct(state)[productCode].map(newChangeObject);
    const lineItemsByType = groupByFn(
      allLineItems,
      (item) => item.get("source"),
      (item) => item,
    );

    const lineItemsWithUpdateableEDD = [
      ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
      ...(lineItemsByType[PackageEntrySource.BULK] || []),
      ...(lineItemsByType[PackageEntrySource.QUOTE] || []),
    ];
    const editedLineItem = allLineItems.find((item) => item.get("id") === packEntryId)!;
    const previousPackCode = editedLineItem.get("packCode");

    const selectedPackage = productDescriptor.availability!.packagingInformation.find(
      (pack) => pack.packCode === packCode,
    )!;
    if (packCode !== previousPackCode) {
      refreshPackage(editedLineItem, selectedPackage);
    }
    editedLineItem.removeMessage(MSG_COULD_NOT_PRESELECT_PACKAGE.key);
    editedLineItem.removeMessage(MSG_PACKAGE_CHANGED.key);
    editedLineItem.removeMessage(MSG_MISSING_PACKAGE_REGULAR.key);

    const salesData = calculateSalesCampaign4PDP({ productDescriptor });
    const salesForPack = salesData?.data?.salePerPack[packCode];
    if (salesForPack) {
      editedLineItem.set("saleCampaignCode", salesForPack.campaignCode);
      editedLineItem.set("saleCampaignEnabled", true);
    } else {
      editedLineItem.set("saleCampaignCode", "");
      editedLineItem.set("saleCampaignEnabled", false);
    }

    const productDescriptors = getDesciptorsForLineItems(state, productCode, allLineItems);
    // 2.2. Then data from PriceInfo objects (price & Country of Origin).
    refreshPriceInfoData(allLineItems, productDescriptors);

    // 3. Refresh the Estimated Delivery Date.
    refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, productDescriptor.availability!);

    validateQty(editedLineItem);
    validateEntireProductQty(allLineItems, productDescriptor, buildCartMaterialNos(state));
    validatePackage(editedLineItem);

    dispatch(sendToRedux(allLineItems));

    dispatch(sendToHybris(allLineItems));
  };
}

export const updateCartCommentThunk = (comment: string, previousComment: string): ThunkResult<void> => {
  return async (dispatch) => {
    apiUpdateCartComment(comment);
    trackCommentActions(comment, previousComment, CommentTargets.CART, comment.length);
    dispatch(updateCartCommentAction(comment));
  };
};

export const UPDATE_CART_COMMENT = "UPDATE_CART_COMMENT";

export const updateCartCommentAction = (comment: string) =>
  ({
    type: UPDATE_CART_COMMENT,
    payload: comment,
  }) as const;
